import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Navigation from '../components/Navigation';
import CSVUpload from '../components/CSVUpload';
import './AdminChapters.css';

function AdminChapters() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [editingChapter, setEditingChapter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    state: '',
    status: 'Active',
    tnfEmailId: '',
    contactEmail: '',
    chairPerson: { name: '', phone: '' },
    secretary: { name: '', phone: '' },
    treasurer: { name: '', phone: '' },
    officersStartDate: '',
    sisterDistricts: ''
  });

  useEffect(() => {
    fetchChapters();
  }, []);

  async function fetchChapters() {
    try {
      const querySnapshot = await getDocs(collection(db, 'chapters'));
      const chaptersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChapters(chaptersList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching chapters:', error);
      setLoading(false);
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleOfficerChange(role, field, value) {
    setFormData(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: value
      }
    }));
  }

  function handleEdit(chapter) {
    setEditingChapter(chapter);
    setFormData({
      name: chapter.name || '',
      state: chapter.state || '',
      status: chapter.status || 'Active',
      tnfEmailId: chapter.tnfEmailId || '',
      contactEmail: chapter.contactEmail || '',
      chairPerson: chapter.chairPerson || { name: '', phone: '' },
      secretary: chapter.secretary || { name: '', phone: '' },
      treasurer: chapter.treasurer || { name: '', phone: '' },
      officersStartDate: chapter.officersStartDate || '',
      sisterDistricts: chapter.sisterDistricts || ''
    });
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingChapter(null);
    setFormData({
      name: '',
      state: '',
      status: 'Active',
      tnfEmailId: '',
      contactEmail: '',
      chairPerson: { name: '', phone: '' },
      secretary: { name: '', phone: '' },
      treasurer: { name: '', phone: '' },
      officersStartDate: '',
      sisterDistricts: ''
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      const chapterData = {
        name: formData.name,
        state: formData.state,
        status: formData.status,
        tnfEmailId: formData.tnfEmailId,
        contactEmail: formData.contactEmail,
        chairPerson: formData.chairPerson,
        secretary: formData.secretary,
        treasurer: formData.treasurer,
        officersStartDate: formData.officersStartDate,
        sisterDistricts: formData.sisterDistricts,
        claimedProjects: editingChapter?.claimedProjects || [],
        updatedAt: serverTimestamp()
      };

      if (editingChapter) {
        // Update existing chapter
        await updateDoc(doc(db, 'chapters', editingChapter.id), chapterData);
        alert('Chapter updated successfully!');
      } else {
        // Create new chapter
        await addDoc(collection(db, 'chapters'), {
          ...chapterData,
          createdAt: serverTimestamp()
        });
        alert('Chapter created successfully!');
      }

      handleCancel();
      fetchChapters();
    } catch (error) {
      console.error('Error saving chapter:', error);
      alert('Failed to save chapter. Please try again.');
    }
  }

  async function handleDelete(chapterId, chapterName) {
    if (!window.confirm(`Are you sure you want to delete ${chapterName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'chapters', chapterId));
      alert('Chapter deleted successfully!');
      fetchChapters();
    } catch (error) {
      console.error('Error deleting chapter:', error);
      alert('Failed to delete chapter.');
    }
  }

  return (
    <div className="admin-container">
      <Navigation />
      
      <div className="admin-content">
        <div className="admin-header">
          <h1>Manage Chapters ({chapters.length})</h1>
          <div className="header-controls">
            <div className="view-toggle">
              <button 
                className={viewMode === 'card' ? 'active' : ''}
                onClick={() => setViewMode('card')}
                title="Card View"
              >
                📇 Cards
              </button>
              <button 
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => setViewMode('table')}
                title="Table View"
              >
                📊 Table
              </button>
            </div>
            <button onClick={() => setShowCSVUpload(!showCSVUpload)} className="csv-button">
              {showCSVUpload ? 'Hide CSV Upload' : '📤 CSV Upload'}
            </button>
            <button onClick={() => setShowForm(!showForm)} className="create-button">
              {showForm ? 'Cancel' : '+ Add New Chapter'}
            </button>
          </div>
        </div>

        {showCSVUpload && (
          <CSVUpload type="chapters" onUploadComplete={fetchChapters} />
        )}

        {showForm && (
          <div className="chapter-form-card">
            <h2>{editingChapter ? 'Edit Chapter' : 'Add New Chapter'}</h2>
            <form onSubmit={handleSubmit} className="chapter-form">
              
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Chapter Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., South Florida Chapter"
                    />
                  </div>

                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., FL"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>TNF Email ID</label>
                    <input
                      type="text"
                      name="tnfEmailId"
                      value={formData.tnfEmailId}
                      onChange={handleInputChange}
                      placeholder="e.g., TNF12345"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Contact Email *</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., southflorida@tnfusa.org"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Chapter Officers</h3>
                
                {/* Chair Person */}
                <div className="officer-section">
                  <h4>Chair Person</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={formData.chairPerson.name}
                        onChange={(e) => handleOfficerChange('chairPerson', 'name', e.target.value)}
                        placeholder="Chair Person Name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={formData.chairPerson.phone}
                        onChange={(e) => handleOfficerChange('chairPerson', 'phone', e.target.value)}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>
                </div>

                {/* Secretary */}
                <div className="officer-section">
                  <h4>Secretary</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={formData.secretary.name}
                        onChange={(e) => handleOfficerChange('secretary', 'name', e.target.value)}
                        placeholder="Secretary Name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={formData.secretary.phone}
                        onChange={(e) => handleOfficerChange('secretary', 'phone', e.target.value)}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>
                </div>

                {/* Treasurer */}
                <div className="officer-section">
                  <h4>Treasurer</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={formData.treasurer.name}
                        onChange={(e) => handleOfficerChange('treasurer', 'name', e.target.value)}
                        placeholder="Treasurer Name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={formData.treasurer.phone}
                        onChange={(e) => handleOfficerChange('treasurer', 'phone', e.target.value)}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Officers 2 Year Start Date</label>
                    <input
                      type="date"
                      name="officersStartDate"
                      value={formData.officersStartDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Additional Information</h3>
                <div className="form-group">
                  <label>Sister District(s)</label>
                  <input
                    type="text"
                    name="sisterDistricts"
                    value={formData.sisterDistricts}
                    onChange={handleInputChange}
                    placeholder="e.g., District 1, District 2"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button">
                  {editingChapter ? 'Update Chapter' : 'Add Chapter'}
                </button>
                <button type="button" onClick={handleCancel} className="cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading chapters...</div>
        ) : viewMode === 'card' ? (
          <div className="chapters-grid">
            {chapters.map(chapter => (
              <div key={chapter.id} className="chapter-card">
                <div className="chapter-card-header">
                  <div className="chapter-title-section">
                    <h3>{chapter.name}</h3>
                    <span className={`status-badge ${chapter.status?.toLowerCase()}`}>
                      {chapter.status || 'Active'}
                    </span>
                  </div>
                  <div className="chapter-actions">
                    <button 
                      onClick={() => handleEdit(chapter)}
                      className="edit-button"
                      title="Edit Chapter"
                    >
                      ✏️
                    </button>
                    <button 
                      onClick={() => handleDelete(chapter.id, chapter.name)}
                      className="delete-button"
                      title="Delete Chapter"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="chapter-info">
                  <div className="info-row">
                    <strong>State:</strong> {chapter.state || 'N/A'}
                  </div>
                  <div className="info-row">
                    <strong>TNF ID:</strong> {chapter.tnfEmailId || 'N/A'}
                  </div>
                  <div className="info-row">
                    <strong>Contact:</strong> {chapter.contactEmail}
                  </div>
                  
                  <div className="officers-section">
                    <h4>Officers</h4>
                    {chapter.chairPerson?.name && (
                      <div className="officer-info">
                        <strong>Chair:</strong> {chapter.chairPerson.name}
                        {chapter.chairPerson.phone && ` (${chapter.chairPerson.phone})`}
                      </div>
                    )}
                    {chapter.secretary?.name && (
                      <div className="officer-info">
                        <strong>Secretary:</strong> {chapter.secretary.name}
                        {chapter.secretary.phone && ` (${chapter.secretary.phone})`}
                      </div>
                    )}
                    {chapter.treasurer?.name && (
                      <div className="officer-info">
                        <strong>Treasurer:</strong> {chapter.treasurer.name}
                        {chapter.treasurer.phone && ` (${chapter.treasurer.phone})`}
                      </div>
                    )}
                  </div>

                  {chapter.sisterDistricts && (
                    <div className="info-row">
                      <strong>Sister Districts:</strong> {chapter.sisterDistricts}
                    </div>
                  )}
                  
                  <div className="info-row">
                    <strong>Projects Claimed:</strong> {chapter.claimedProjects?.length || 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="table-container">
            <table className="chapters-table">
              <thead>
                <tr>
                  <th>Chapter</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Email</th>
                  <th>Chair</th>
                  <th>Projects</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {chapters.map(chapter => (
                  <tr key={chapter.id}>
                    <td className="chapter-name">{chapter.name}</td>
                    <td>{chapter.state}</td>
                    <td>
                      <span className={`status-badge ${chapter.status?.toLowerCase()}`}>
                        {chapter.status || 'Active'}
                      </span>
                    </td>
                    <td className="email-cell">{chapter.contactEmail}</td>
                    <td>{chapter.chairPerson?.name || 'N/A'}</td>
                    <td>{chapter.claimedProjects?.length || 0}</td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => handleEdit(chapter)}
                        className="edit-button-small"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => handleDelete(chapter.id, chapter.name)}
                        className="delete-button-small"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminChapters;
